/**
 * RenderRouter — Dynamically selects between rendering engines based on
 * the scene_manifest from the backend.
 *
 * Two-level dispatch:
 *   scene_type   → which engine (phaser_2d, three_3d, content_panel, ...)
 *   renderer_key → which scene class within that engine (looked up from RendererRegistry)
 *
 * Adding a new domain:
 *   1. Create a scene class (Phaser or Three.js)
 *   2. Call RendererRegistry.register('my_domain', MyScene, 'phaser_2d' | 'three_3d')
 *   3. Set renderer_key and scene_type in the domain's scene manifest YAML
 *
 * Orientation: spatial renderers (phaser_2d, three_3d) accept an optional
 * `orientationOverride` that bypasses the active ViewportOrientation
 * context. Used by the Scene Designer's "view as learner" preview so
 * the coach can sanity-check the landscape framing without flipping
 * their persisted preference.
 *
 * Phase 12 (June 2026) — `interactive_quiz` is the unified quiz scene
 * type. Spatial formats (static_spotlight, animated_board) route to
 * the InteractiveQuizPlayer wrapper which mounts ThreeGame plus the
 * Phase 12 telemetry / teaching-video chaining hook. Non-spatial
 * `simple_qna` falls through to children (SessionPage's QuestionPanel
 * branch handles it). The same wrapper handles legacy `quiz_panel`
 * scenes that haven't migrated yet — they look like simple_qna.
 */

import React, { lazy, Suspense } from 'react';
import PhaserGame from './PhaserGame';
import ThreeGame from './ThreeGame';
import InteractiveQuizPlayer, {
  resolveQuizFormat,
} from '../components/quiz/InteractiveQuizPlayer';

import './scenes/FootballScene';
import './three/FootballScene3D';

const TimedTestSessionPlayer = lazy(() =>
  import('../components/test-prep/TimedTestSessionPlayer')
);
const PassageQuestionSetPlayer = lazy(() =>
  import('../components/test-prep/PassageQuestionSetPlayer')
);
const ScoreReportRenderer = lazy(() =>
  import('../components/test-prep/ScoreReportRenderer')
);
const TestReviewPlayer = lazy(() =>
  import('../components/test-prep/TestReviewPlayer')
);

const TestPrepFallback = () => (
  <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
    Loading test prep…
  </div>
);

const RenderRouter = ({
  sceneManifest,
  rendererConfig,
  actors,
  frame,
  scene,
  feedback,
  children,
  orientationOverride,
}) => {
  if (!sceneManifest) {
    return <>{children}</>;
  }

  const { scene_type, renderer_key } = sceneManifest;

  switch (scene_type) {
    case 'phaser_2d':
      return (
        <PhaserGame
          manifest={sceneManifest}
          actors={actors}
          frame={frame}
          sceneKey={renderer_key || 'base'}
          orientationOverride={orientationOverride}
        />
      );

    case 'three_3d':
      return (
        <ThreeGame
          manifest={sceneManifest}
          actors={actors}
          frame={frame}
          sceneKey={renderer_key || 'football_3d'}
          orientationOverride={orientationOverride}
        />
      );

    case 'interactive_quiz':
      return (
        <InteractiveQuizPlayer
          sceneManifest={sceneManifest}
          rendererConfig={rendererConfig}
          actors={actors}
          frame={frame}
          scene={scene}
          feedback={feedback}
          orientationOverride={orientationOverride}
        >
          {children}
        </InteractiveQuizPlayer>
      );

    case 'timed_test_session':
      return (
        <Suspense fallback={<TestPrepFallback />}>
          <TimedTestSessionPlayer
            sceneManifest={sceneManifest}
            rendererConfig={rendererConfig}
            scene={scene}
            feedback={feedback}
          >
            {children}
          </TimedTestSessionPlayer>
        </Suspense>
      );

    case 'passage_question_set':
      return (
        <Suspense fallback={<TestPrepFallback />}>
          <PassageQuestionSetPlayer
            sceneManifest={sceneManifest}
            rendererConfig={rendererConfig}
            scene={scene}
            feedback={feedback}
          >
            {children}
          </PassageQuestionSetPlayer>
        </Suspense>
      );

    case 'score_report':
      return (
        <Suspense fallback={<TestPrepFallback />}>
          <ScoreReportRenderer
            sceneManifest={sceneManifest}
            rendererConfig={rendererConfig}
            scene={scene}
          />
        </Suspense>
      );

    case 'test_review':
      return (
        <Suspense fallback={<TestPrepFallback />}>
          <TestReviewPlayer
            sceneManifest={sceneManifest}
            rendererConfig={rendererConfig}
            scene={scene}
            feedback={feedback}
          >
            {children}
          </TestReviewPlayer>
        </Suspense>
      );

    case 'content_panel':
    case 'classroom':
    case 'quiz_panel':           // legacy — auto-renamed by migration 0210
    default:
      return <>{children}</>;
  }
};

export default RenderRouter;
